import React, {useCallback, useEffect, useRef, useState} from 'react';
import {FmlxBreadcrumbs, FmlxTooltip} from 'fmlx-common-ui';
import PropType from 'prop-types';
import './BreadCrumbs.scss';
import useDragDropProtocol from './useDragDropProtocol';
import {ExplorerContentTypes} from '../../constants/dataEnum';

function DroppableBreadcrumbItem({item, onDropped, itemIndex, onDragOver, isDroppable}) {
  const protocol = {
    ID: item.protocolFolderId,
    type: ExplorerContentTypes.FOLDER,
  };

  const prevIsDragOver = useRef(false);
  const {drop, isDragOver} = useDragDropProtocol({protocol, onDropped});

  useEffect(() => {
    if (prevIsDragOver.current === isDragOver) return;

    if (isDragOver) {
      onDragOver(itemIndex);
    } else {
      onDragOver(0);
    }
    prevIsDragOver.current = isDragOver;
  }, [isDragOver, onDragOver, itemIndex]);

  const setBreadcrumbRef = (e) => {
    if (isDroppable && e?.parentNode?.parentNode) {
      // set to grand parent node to enable drop to icon and label
      drop(e.parentNode.parentNode);
    }
  };

  return (
    <span className="breadcrumb-item" ref={setBreadcrumbRef}>
      <FmlxTooltip title={item.label} placement="bottom" className="text-clamp">
        {isDragOver && (
          <div className="drag-over-tooltip text-clamp">
            Move to&nbsp;
            {item.label}
          </div>
        )}
        {item.label}
      </FmlxTooltip>
    </span>
  );
}

DroppableBreadcrumbItem.propTypes = {
  item: PropType.any.isRequired,
  onDropped: PropType.func.isRequired,
  onDragOver: PropType.func.isRequired,
  itemIndex: PropType.number.isRequired,
  isDroppable: PropType.bool,
};

DroppableBreadcrumbItem.defaultProps = {
  isDroppable: true,
};

// TODO: rename this component to be more specific
function BreadCrumbs({items, show, className, onChange, onProtocolDropped}) {
  const [dragOverIndex, setDragOverIndex] = useState(0);

  const handleDragOver = useCallback((index) => {
    setDragOverIndex(index);
  }, []);

  const activeItemId = items[items.length - 1].id;

  return show ? (
    <div
      className={[
        'protocol-explorer-breadcrumbs',
        dragOverIndex > 0 ? `drag-over-${dragOverIndex}` : '',
        `${className}`,
      ].join(' ')}
    >
      <FmlxBreadcrumbs
        collapsed={false}
        activeItem={activeItemId}
        items={items.map((item, idx) => ({
          ...item,
          label: (
            <DroppableBreadcrumbItem
              item={item}
              onDropped={onProtocolDropped}
              itemIndex={idx + 1}
              onDragOver={handleDragOver}
              isDroppable={item?.id !== activeItemId}
            />
          ),
        }))}
        onChange={onChange}
      />
    </div>
  ) : null;
}

BreadCrumbs.propTypes = {
  items: PropType.arrayOf(
    PropType.shape({
      id: PropType.any,
      label: PropType.string,
      protocolFolderId: PropType.any,
      icon: PropType.node,
    }),
  ),
  show: PropType.bool,
  className: PropType.string,
  onChange: PropType.func,
  onProtocolDropped: PropType.func,
};

BreadCrumbs.defaultProps = {
  items: [],
  show: true,
  className: '',
  onChange: () => {},
  onProtocolDropped: () => {},
};

export default BreadCrumbs;
